import { useState, useRef, useCallback, useEffect } from "react";
import { X, Check, Play, Pause, Scissors } from "lucide-react";

interface VideoTrimModalProps {
  isOpen: boolean;
  videoSrc: string;
  videoDuration: number;
  onClose: () => void;
  onTrimComplete: (trimmedBlob: Blob, duration: number) => void;
  maxDuration?: number;
  minDuration?: number;
}

const VideoTrimModal = ({
  isOpen,
  videoSrc,
  videoDuration,
  onClose,
  onTrimComplete,
  maxDuration = 15,
  minDuration = 5,
}: VideoTrimModalProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [startTime, setStartTime] = useState(0);
  const [endTime, setEndTime] = useState(Math.min(videoDuration, maxDuration));
  const [dragging, setDragging] = useState<"start" | "end" | "playhead" | null>(null);
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const [trimming, setTrimming] = useState(false);

  const trimDuration = endTime - startTime;
  const isValidDuration = trimDuration >= minDuration && trimDuration <= maxDuration;

  // Generate thumbnails
  useEffect(() => {
    if (!isOpen || !videoSrc) return;

    const generateThumbnails = async () => {
      const video = document.createElement("video");
      video.src = videoSrc;
      video.crossOrigin = "anonymous";

      await new Promise((resolve) => {
        video.onloadedmetadata = resolve;
      });

      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = 60;
      canvas.height = 80;

      const thumbCount = 10;
      const interval = video.duration / thumbCount;
      const newThumbnails: string[] = [];

      for (let i = 0; i < thumbCount; i++) {
        video.currentTime = i * interval;
        await new Promise((resolve) => {
          video.onseeked = resolve;
        });
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        newThumbnails.push(canvas.toDataURL("image/jpeg", 0.5));
      }

      setThumbnails(newThumbnails);
    };

    generateThumbnails();
  }, [isOpen, videoSrc]);

  // Update current time display
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);

      // Loop within trim range
      if (video.currentTime >= endTime) {
        video.currentTime = startTime;
      }
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => video.removeEventListener("timeupdate", handleTimeUpdate);
  }, [startTime, endTime]);

  // Set initial end time based on video duration
  useEffect(() => {
    if (isOpen && videoDuration > 0) {
      setEndTime(Math.min(videoDuration, maxDuration));
      setStartTime(0);
    }
  }, [isOpen, videoDuration, maxDuration]);

  const togglePlayPause = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      if (video.currentTime < startTime || video.currentTime >= endTime) {
        video.currentTime = startTime;
      }
      video.play();
    }
    setIsPlaying(!isPlaying);
  }, [isPlaying, startTime, endTime]);

  const handleTimelineClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!timelineRef.current || dragging) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * videoDuration;

      if (videoRef.current) {
        videoRef.current.currentTime = Math.max(startTime, Math.min(endTime, newTime));
      }
    },
    [videoDuration, startTime, endTime, dragging]
  );

  const handleDragStart = useCallback(
    (type: "start" | "end" | "playhead") => (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragging(type);
    },
    []
  );

  const handleDrag = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!dragging || !timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
      const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const newTime = percentage * videoDuration;

      if (dragging === "start") {
        const maxStart = endTime - minDuration;
        setStartTime(Math.max(0, Math.min(maxStart, newTime)));
      } else if (dragging === "end") {
        const minEnd = startTime + minDuration;
        const maxEnd = Math.min(videoDuration, startTime + maxDuration);
        setEndTime(Math.max(minEnd, Math.min(maxEnd, newTime)));
      } else if (dragging === "playhead" && videoRef.current) {
        videoRef.current.currentTime = Math.max(startTime, Math.min(endTime, newTime));
      }
    },
    [dragging, videoDuration, startTime, endTime, minDuration, maxDuration]
  );

  const handleDragEnd = useCallback(() => {
    setDragging(null);
  }, []);

  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleDrag);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDrag);
      window.addEventListener("touchend", handleDragEnd);
    }

    return () => {
      window.removeEventListener("mousemove", handleDrag);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDrag);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [dragging, handleDrag, handleDragEnd]);

  const handleTrim = useCallback(async () => {
    if (!videoRef.current || !isValidDuration) return;

    setTrimming(true);

    try {
      // Create a MediaRecorder to capture the trimmed portion
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      // Use MediaRecorder with canvas
      const stream = canvas.captureStream(30);
      
      // Add audio track if available
      const audioContext = new AudioContext();
      const source = audioContext.createMediaElementSource(video);
      const destination = audioContext.createMediaStreamDestination();
      source.connect(destination);
      source.connect(audioContext.destination);
      
      destination.stream.getAudioTracks().forEach(track => {
        stream.addTrack(track);
      });

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
          ? "video/webm;codecs=vp9"
          : "video/webm",
      });

      const chunks: Blob[] = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      video.currentTime = startTime;
      await new Promise((resolve) => {
        video.onseeked = resolve;
      });

      mediaRecorder.start();
      video.play();

      // Draw frames to canvas
      const drawFrame = () => {
        if (video.currentTime >= endTime) {
          video.pause();
          mediaRecorder.stop();
          return;
        }
        ctx.drawImage(video, 0, 0);
        requestAnimationFrame(drawFrame);
      };

      drawFrame();

      await new Promise<void>((resolve) => {
        mediaRecorder.onstop = () => resolve();
      });

      const blob = new Blob(chunks, { type: "video/webm" });
      onTrimComplete(blob, trimDuration);
    } catch (error) {
      console.error("Trim error:", error);
      
      // Fallback: Just use the original video with time markers
      // In production, you'd use FFmpeg.wasm or a server-side solution
      const response = await fetch(videoSrc);
      const blob = await response.blob();
      onTrimComplete(blob, trimDuration);
    } finally {
      setTrimming(false);
    }
  }, [startTime, endTime, isValidDuration, trimDuration, videoSrc, onTrimComplete]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, "0")}.${ms}`;
  };

  if (!isOpen) return null;

  const startPercent = (startTime / videoDuration) * 100;
  const endPercent = (endTime / videoDuration) * 100;
  const playheadPercent = (currentTime / videoDuration) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Hidden canvas for processing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        <div className="text-center">
          <h2 className="text-white font-medium">Trim Video</h2>
          <p className={`text-xs ${isValidDuration ? "text-white/60" : "text-destructive"}`}>
            {formatTime(trimDuration)} / {maxDuration}s max
          </p>
        </div>

        <button
          onClick={handleTrim}
          disabled={!isValidDuration || trimming}
          className="p-2 rounded-full bg-pop hover:bg-pop/80 transition-colors disabled:opacity-50"
        >
          {trimming ? (
            <div className="w-6 h-6 rounded-full border-2 border-white border-t-transparent animate-spin" />
          ) : (
            <Check className="w-6 h-6 text-white" />
          )}
        </button>
      </div>

      {/* Video Preview */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative max-h-full aspect-[9/16] bg-black rounded-2xl overflow-hidden">
          <video
            ref={videoRef}
            src={videoSrc}
            className="w-full h-full object-contain"
            playsInline
            onEnded={() => setIsPlaying(false)}
          />

          {/* Play/Pause overlay */}
          <button
            onClick={togglePlayPause}
            className="absolute inset-0 flex items-center justify-center"
          >
            {!isPlaying && (
              <div className="p-4 rounded-full bg-black/50 backdrop-blur-sm">
                <Play className="w-8 h-8 text-white fill-white" />
              </div>
            )}
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="p-4 bg-black/80">
        <div className="mb-4">
          <div
            ref={timelineRef}
            className="relative h-16 rounded-lg overflow-hidden cursor-pointer"
            onClick={handleTimelineClick}
          >
            {/* Thumbnails */}
            <div className="absolute inset-0 flex">
              {thumbnails.map((thumb, i) => (
                <div key={i} className="flex-1 h-full">
                  <img
                    src={thumb}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
              {thumbnails.length === 0 && (
                <div className="w-full h-full bg-muted animate-pulse" />
              )}
            </div>

            {/* Dimmed areas outside trim range */}
            <div
              className="absolute inset-y-0 left-0 bg-black/70"
              style={{ width: `${startPercent}%` }}
            />
            <div
              className="absolute inset-y-0 right-0 bg-black/70"
              style={{ width: `${100 - endPercent}%` }}
            />

            {/* Trim selection border */}
            <div
              className="absolute inset-y-0 border-2 border-pop rounded"
              style={{
                left: `${startPercent}%`,
                width: `${endPercent - startPercent}%`,
              }}
            />

            {/* Start handle */}
            <div
              className="absolute inset-y-0 w-4 bg-pop cursor-ew-resize flex items-center justify-center touch-none"
              style={{ left: `calc(${startPercent}% - 8px)` }}
              onMouseDown={handleDragStart("start")}
              onTouchStart={handleDragStart("start")}
            >
              <div className="w-1 h-8 bg-white rounded-full" />
            </div>

            {/* End handle */}
            <div
              className="absolute inset-y-0 w-4 bg-pop cursor-ew-resize flex items-center justify-center touch-none"
              style={{ left: `calc(${endPercent}% - 8px)` }}
              onMouseDown={handleDragStart("end")}
              onTouchStart={handleDragStart("end")}
            >
              <div className="w-1 h-8 bg-white rounded-full" />
            </div>

            {/* Playhead */}
            <div
              className="absolute inset-y-0 w-0.5 bg-white shadow-lg cursor-ew-resize touch-none"
              style={{ left: `${playheadPercent}%` }}
              onMouseDown={handleDragStart("playhead")}
              onTouchStart={handleDragStart("playhead")}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rounded-full" />
            </div>
          </div>
        </div>

        {/* Time indicators */}
        <div className="flex items-center justify-between text-xs text-white/60">
          <span>Start: {formatTime(startTime)}</span>
          <div className="flex items-center gap-2">
            <Scissors className="w-4 h-4" />
            <span className={isValidDuration ? "text-pop" : "text-destructive"}>
              {formatTime(trimDuration)}
            </span>
          </div>
          <span>End: {formatTime(endTime)}</span>
        </div>

        {!isValidDuration && (
          <p className="text-center text-destructive text-xs mt-2">
            Video must be between {minDuration}-{maxDuration} seconds
          </p>
        )}
      </div>
    </div>
  );
};

export default VideoTrimModal;
